import React, { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { fetchProjectMilestones, createMilestone, updateMilestone, deleteMilestone } from '../../store/slices/milestonesSlice';
import Button from '../UI/Button';
import Select from '../UI/Select';
import LoadingSpinner from '../UI/LoadingSpinner';
import {
  MilestoneCard,
  MilestoneForm,
  Milestone,
  MilestoneFormData,
} from './Milestones/index';

interface MilestonesEditorProps {
  projectId?: string;
  milestones?: Milestone[]; // Optional: for backward compatibility or local state
  onChange?: (milestones: Milestone[]) => void; // Optional: for backward compatibility
  disabled?: boolean;
}

const MilestonesEditor: React.FC<MilestonesEditorProps> = ({
  projectId,
  milestones: propMilestones,
  onChange,
  disabled = false,
}) => {
  const dispatch = useAppDispatch();
  const { items, projectMilestones, isLoading } = useAppSelector((state) => state.milestones);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingMilestoneId, setEditingMilestoneId] = useState<string | null>(null);
  const [milestoneForm, setMilestoneForm] = useState<Partial<MilestoneFormData>>({});
  const [isSaving, setIsSaving] = useState(false);

  // Fetch milestones from API if projectId is provided
  useEffect(() => {
    if (projectId && !propMilestones) {
      dispatch(fetchProjectMilestones({ projectId, includeDeleted: false }));
    }
  }, [dispatch, projectId, propMilestones]);

  // Use prop milestones if provided (backward compatibility), otherwise use Redux state
  const milestones = propMilestones || (projectId && projectMilestones[projectId]
    ? projectMilestones[projectId].map((id) => items[id]).filter(Boolean) as Milestone[]
    : []);

  const handleFormDataChange = (data: Partial<MilestoneFormData>) => {
    setMilestoneForm((prev) => ({ ...prev, ...data }));
  };

  const handleAdd = async () => {
    if (!milestoneForm.title || !milestoneForm.scheduledDate) return;
    if (!projectId) {
      // Fallback to local state if no projectId (for new projects)
      const newMilestone: Milestone = {
        title: milestoneForm.title,
        description: milestoneForm.description || '',
        scheduledDate: milestoneForm.scheduledDate,
        status: milestoneForm.status || 'pending',
        delayReason: milestoneForm.delayReason,
        earlyCompletion: milestoneForm.earlyCompletion || false,
        earlyCompletionReason: milestoneForm.earlyCompletionReason,
      };
      if (onChange) {
        onChange([...milestones, newMilestone]);
      }
      setMilestoneForm({});
      setEditingIndex(null);
      return;
    }

    setIsSaving(true);
    try {
      const milestoneData: Partial<Milestone> & { projectId: string } = {
        projectId,
        title: milestoneForm.title!,
        description: milestoneForm.description,
        scheduledDate: milestoneForm.scheduledDate,
        status: milestoneForm.status || 'pending',
        delayReason: milestoneForm.delayReason,
        earlyCompletion: milestoneForm.earlyCompletion || false,
        earlyCompletionReason: milestoneForm.earlyCompletionReason,
      };
      await dispatch(createMilestone(milestoneData));
      setMilestoneForm({});
      setEditingIndex(null);
    } catch (error) {
      console.error('Error creating milestone:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (milestone: Milestone) => {
    const milestoneId = milestone._id || milestone.id || '';
    setEditingMilestoneId(milestoneId);
    setEditingIndex(milestones.findIndex((m) => (m._id || m.id) === milestoneId));
    setMilestoneForm({
      _id: milestone._id,
      id: milestone.id,
      title: milestone.title,
      description: milestone.description || '',
      scheduledDate: milestone.scheduledDate instanceof Date
        ? milestone.scheduledDate.toISOString().split('T')[0]
        : typeof milestone.scheduledDate === 'string'
        ? milestone.scheduledDate.split('T')[0]
        : '',
      status: milestone.status,
      delayReason: milestone.delayReason || '',
      earlyCompletion: milestone.earlyCompletion || false,
      earlyCompletionReason: milestone.earlyCompletionReason || '',
    });
  };

  const handleSave = async () => {
    if (!milestoneForm.title || !milestoneForm.scheduledDate) return;
    
    if (!projectId || !editingMilestoneId) {
      // Fallback to local state if no projectId (for new projects)
      if (editingIndex === null || editingIndex < 0) return;
      const updated = [...milestones];
      const existingMilestone = updated[editingIndex];
      
      updated[editingIndex] = {
        ...existingMilestone,
        title: milestoneForm.title || existingMilestone.title,
        description: milestoneForm.description !== undefined ? milestoneForm.description : existingMilestone.description,
        scheduledDate: milestoneForm.scheduledDate
          ? milestoneForm.scheduledDate
          : existingMilestone.scheduledDate,
        status: milestoneForm.status || existingMilestone.status,
        delayReason: milestoneForm.delayReason !== undefined ? milestoneForm.delayReason : existingMilestone.delayReason,
        earlyCompletion: milestoneForm.earlyCompletion !== undefined ? milestoneForm.earlyCompletion : existingMilestone.earlyCompletion,
        earlyCompletionReason: milestoneForm.earlyCompletionReason !== undefined ? milestoneForm.earlyCompletionReason : existingMilestone.earlyCompletionReason,
      };

      if (onChange) {
        onChange(updated);
      }
      setEditingIndex(null);
      setEditingMilestoneId(null);
      setMilestoneForm({});
      return;
    }

    setIsSaving(true);
    try {
      // Build update data, only including defined values
      const updateData: Partial<Milestone> = {};
      
      if (milestoneForm.title !== undefined) {
        updateData.title = milestoneForm.title;
      }
      if (milestoneForm.description !== undefined) {
        updateData.description = milestoneForm.description;
      }
      if (milestoneForm.scheduledDate !== undefined) {
        updateData.scheduledDate = milestoneForm.scheduledDate;
      }
      if (milestoneForm.status !== undefined) {
        updateData.status = milestoneForm.status;
      }
      if (milestoneForm.delayReason !== undefined) {
        updateData.delayReason = milestoneForm.delayReason;
      }
      if (milestoneForm.earlyCompletion !== undefined) {
        updateData.earlyCompletion = milestoneForm.earlyCompletion;
      }
      if (milestoneForm.earlyCompletionReason !== undefined) {
        updateData.earlyCompletionReason = milestoneForm.earlyCompletionReason;
      }
      
      // Auto-set completedDate when status changes to completed
      if (updateData.status === 'completed') {
        const existingMilestone = milestones.find((m) => (m._id || m.id) === editingMilestoneId);
        if (existingMilestone && !existingMilestone.completedDate) {
          updateData.completedDate = new Date().toISOString();
        }
      }
      
      // Filter out undefined values before sending
      const cleanUpdateData = Object.fromEntries(
        Object.entries(updateData).filter(([_, value]) => value !== undefined)
      ) as Partial<Milestone>;

      await dispatch(updateMilestone({ id: editingMilestoneId, data: cleanUpdateData })).unwrap();
      
      // Refresh milestones list after successful update
      if (projectId) {
        await dispatch(fetchProjectMilestones({ projectId, includeDeleted: false }));
      }
      
      setEditingIndex(null);
      setEditingMilestoneId(null);
      setMilestoneForm({});
    } catch (error) {
      console.error('Error updating milestone:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingIndex(null);
    setEditingMilestoneId(null);
    setMilestoneForm({});
  };

  const handleDelete = async (milestone: Milestone) => {
    if (!confirm('Are you sure you want to delete this milestone?')) return;
    
    const milestoneId = milestone._id || milestone.id || '';
    
    if (!projectId || !milestoneId) {
      // Fallback to local state if no projectId (for new projects)
      const index = milestones.findIndex((m) => (m._id || m.id) === milestoneId);
      if (index >= 0 && onChange) {
        const updated = milestones.filter((_, i) => i !== index);
        onChange(updated);
      }
      return;
    }

    try {
      await dispatch(deleteMilestone(milestoneId));
    } catch (error) {
      console.error('Error deleting milestone:', error);
    }
  };

  const handleUpdateStatus = async (milestone: Milestone, status: Milestone['status']) => {
    const milestoneId = milestone._id || milestone.id || '';
    
    if (!projectId || !milestoneId) {
      // Fallback to local state if no projectId (for new projects)
      const index = milestones.findIndex((m) => (m._id || m.id) === milestoneId);
      if (index >= 0) {
        const updated = [...milestones];
        updated[index] = {
          ...milestone,
          status,
          completedDate: status === 'completed' && !milestone.completedDate 
            ? new Date().toISOString() 
            : milestone.completedDate,
        };
        if (onChange) {
          onChange(updated);
        }
      }
      return;
    }

    try {
      const updateData: Partial<Milestone> = {
        status,
      };
      
      if (status === 'completed' && !milestone.completedDate) {
        updateData.completedDate = new Date().toISOString();
      }
      
      await dispatch(updateMilestone({ id: milestoneId, data: updateData }));
    } catch (error) {
      console.error('Error updating milestone status:', error);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Project Milestones</h3>
        {!disabled && editingIndex === null && (
          <Button
            type="button"
            size="sm"
            onClick={() => {
              setMilestoneForm({
                title: '',
                description: '',
                scheduledDate: new Date().toISOString().split('T')[0],
                status: 'pending',
              });
              setEditingIndex(-1); // Use -1 to indicate new milestone
            }}
          >
            Add Milestone
          </Button>
        )}
      </div>

      {(editingIndex === -1 || editingIndex !== null) && !disabled && (
        <MilestoneForm
          formData={{
            title: milestoneForm.title || '',
            description: milestoneForm.description,
            scheduledDate: milestoneForm.scheduledDate || new Date().toISOString().split('T')[0],
            status: milestoneForm.status || 'pending',
            delayReason: milestoneForm.delayReason,
            earlyCompletion: milestoneForm.earlyCompletion,
            earlyCompletionReason: milestoneForm.earlyCompletionReason,
            ...(milestoneForm._id && { _id: milestoneForm._id }),
            ...(milestoneForm.id && { id: milestoneForm.id }),
          } as MilestoneFormData}
          onFormDataChange={handleFormDataChange}
          onSubmit={editingIndex === -1 ? handleAdd : handleSave}
          onCancel={handleCancel}
          isLoading={isSaving}
          submitLabel={editingIndex === -1 ? 'Add' : 'Save'}
        />
      )}

      {isLoading && !propMilestones ? (
        <LoadingSpinner size="sm" text="Loading milestones..." />
      ) : milestones.length === 0 ? (
        <p className="text-gray-500 text-sm">No milestones defined yet.</p>
      ) : (
        <div className="space-y-3">
          {milestones.map((milestone, index) => {
            const milestoneId = milestone._id || milestone.id || '';
            const isEditing = editingIndex === index;
            return (
              <MilestoneCard
                key={milestoneId || index}
                milestone={milestone}
                showActions={!disabled && !isEditing}
                actions={
                  !isEditing ? (
                    <>
                      <div className="w-32">
                        <Select
                          value={milestone.status}
                          onChange={(e) =>
                            handleUpdateStatus(milestone, e.target.value as Milestone['status'])
                          }
                          options={[
                            { value: 'pending', label: 'Pending' },
                            { value: 'in_progress', label: 'In Progress' },
                            { value: 'completed', label: 'Completed' },
                            { value: 'delayed', label: 'Delayed' },
                          ]}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(milestone)}
                      >
                        Edit
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(milestone)}
                      >
                        Delete
                      </Button>
                    </>
                  ) : undefined
                }
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MilestonesEditor;

